import React, { useCallback, useEffect, useRef, useState } from "react";
import { eventBus } from "../../utils/eventBus";
import { errInfo, fmtDate } from "../staff/staffUtils";
import { internalApi } from "./internalApi";
import "../staff/staff.css";
import "./internal.css";

export const LANG_LABEL = { en: "English", es: "Español" };
// The chart's language in English words (what the chart shows).
export const CHART_LABEL = { en: "English", es: "Spanish" };
export const langLabel = (code) => LANG_LABEL[code] || "";

/*
 * The patient's forms language (GET /patients/{id}/forms-language):
 *   data = {saved: {language, source, updated_by, updated_at, request_id}|null,
 *           chart: "en"|"es"|null, last_used, practice_default, suggested,
 *           suggested_from: "saved"|"chart"|"practice", installed}
 *   null while loading, and when the server has no such route (404/error):
 *   callers then behave as before (nothing is saved).
 * Re-reads after a send or a change for this patient (events
 * formwiz:requests-changed, formwiz:language-changed).
 */
export function useFormsLanguage(patientid) {
  // {pid, data, error}: what was last read, and for which patient. `loading`
  // is true from the first render for a new patient (callers wait for it
  // before they mount a renderer with the language).
  const [st, setSt] = useState({ pid: null, data: null, error: null });
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);

  const reload = useCallback(async () => {
    const mine = ++seq.current;
    if (!patientid) {
      setSt({ pid: null, data: null, error: null });
      setRefreshing(false);
      return null;
    }
    setRefreshing(true);
    try {
      const d = await internalApi.language(patientid);
      if (mine !== seq.current) return null;
      const good = d && typeof d === "object" && !Array.isArray(d) ? d : null;
      setSt({ pid: patientid, data: good, error: null });
      return good;
    } catch (e) {
      if (mine !== seq.current) return null;
      setSt({ pid: patientid, data: null, error: errInfo(e) });
      return null;
    } finally {
      if (mine === seq.current) setRefreshing(false);
    }
  }, [patientid]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    if (!patientid) return undefined;
    const h = (e) => {
      if (!e || !e.patientid || String(e.patientid) === String(patientid)) reload();
    };
    eventBus.on("formwiz:requests-changed", h);
    eventBus.on("formwiz:language-changed", h);
    return () => {
      eventBus.off("formwiz:requests-changed", h);
      eventBus.off("formwiz:language-changed", h);
    };
  }, [patientid, reload]);

  const current = !!patientid && st.pid === patientid;
  return {
    data: current ? st.data : null,
    error: current ? st.error : null,
    loading: !!patientid && (!current || refreshing),
    reload,
  };
}

// The patient's own language (saved, else the chart's), or null.
export function patientLanguage(data) {
  if (!data) return null;
  if (data.saved && (data.saved.language === "en" || data.saved.language === "es")) return data.saved.language;
  if (data.chart === "en" || data.chart === "es") return data.chart;
  return null;
}

// Where the chip's language comes from, in words (its tooltip).
export function languageSourceText(data) {
  if (!data) return "";
  if (data.saved) {
    const by = data.saved.updated_by ? `by ${data.saved.updated_by}` : "";
    const at = data.saved.updated_at ? fmtDate(data.saved.updated_at) : "";
    const how = data.saved.source === "manual" ? "Set" : "Saved when forms were sent";
    return [how, by, at ? `on ${at}` : ""].filter(Boolean).join(" ");
  }
  if (data.chart) return "From the chart";
  return "Practice default";
}

/*
 * "Forms language: Español" with a small Change menu (EN/ES: PUT, saved as
 * source "manual"). Hidden when the server doesn't have the feature, and when
 * the tables are not installed and the chart says nothing.
 */
export function FormsLanguageChip({ patientid, onChanged }) {
  const { data, reload } = useFormsLanguage(patientid);
  const [menu, setMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const wrap = useRef(null);

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setMenu(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menu]);

  if (!data) return null;
  const installed = data.installed !== false;
  if (!installed && !data.chart) return null;
  const lang = patientLanguage(data) || data.suggested || data.practice_default || "en";
  const pick = async (l) => {
    setMenu(false);
    if (data.saved && data.saved.language === l) return;
    setBusy(true);
    setErr("");
    try {
      await internalApi.setLanguage(patientid, l);
      await reload();
      eventBus.emit("formwiz:language-changed", { patientid });
      if (onChanged) onChanged(l);
    } catch (e) {
      setErr(errInfo(e).status === 503 ? "Not available yet" : "Couldn't save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="fwi-lang" ref={wrap} data-testid="forms-language-chip">
      <span className={`fws-chip ${data.saved ? "fws-tone-info" : "fws-tone-muted"}`} title={languageSourceText(data)}>
        Forms language: <b style={{ marginLeft: 3 }}>{langLabel(lang)}</b>
        {!data.saved && <span className="fws-small" style={{ marginLeft: 4 }}>({data.chart ? "chart" : "default"})</span>}
      </span>
      {installed && (
        <button type="button" className="fws-link fws-small" aria-haspopup="menu" aria-expanded={menu} disabled={busy}
          onClick={() => setMenu((m) => !m)}>
          {busy ? "Saving…" : "Change"}
        </button>
      )}
      {menu && (
        <span className="fwi-lang-menu" role="menu" aria-label="Forms language">
          {["en", "es"].map((l) => (
            <button key={l} type="button" role="menuitemradio" aria-checked={lang === l} onClick={() => pick(l)}>
              {langLabel(l)}
            </button>
          ))}
        </span>
      )}
      {err && <span className="fws-err fws-small">{err}</span>}
    </span>
  );
}

export default FormsLanguageChip;
